import { ColorRange, Color } from '@methanesat/colors';
import { ScaleLinear, scaleLinear } from 'd3-scale';

export const makeLinearColorScale = (domain: number[], colors: ColorRange): ScaleLinear<Color, Color> => {
    return scaleLinear<Color, Color>().domain(domain).range(colors);
};

/** Generates an array of colors sampled from a linear color scale */
export const createLinearColorScaleArray = (stops: number, colors: ColorRange) => {
    const colorScale = makeLinearColorScale([0, 1], colors);
    const colorArray: Color[] = [];
    for (let i = 0; i < stops; i++) {
        const val = i / (stops - 1);
        const color = [...colorScale(val)] as Color;
        colorArray.push(color);
    }
    return colorArray;
};

/**
 * Returns the closest color from the input palette for a given emissions value.
 * Finds the color whose anchor percentage is nearest to the value's normalized position between min and max.
 * @param val: Emission value that you'd like to get the color for
 * @param min: Min emissions value
 * @param max: Max emissions value
 * @param colors: Array of rgb() strings. The palette to retrieve the color from.
 * @param colorScaleBins: Color scale bins (each value is a max threshold, ie with bin 4 = 1.5, bin 5 = 2.25 a value of 1.6 should go to bin 5)
 * @param anchors: Colorbar anchor positions for the `colors` palette
 * @returns rgb() color string
 */
export const getDispersedColorFromMethaneValue = (
    val: number,
    min: number,
    max: number,
    colors: string[],
    colorScaleBins: number[] | undefined,
    anchors: number[]
) => {
    //color scale bins will only be passed for l4 rasters currently, everything else should use original linear scale logic
    if (!colorScaleBins) {
        const clampedValue = Math.max(min, Math.min(max, val));
        const percentage = ((clampedValue - min) / (max - min)) * 100;
        let nearestIndex = 0;
        let minDiff = Math.abs(anchors[nearestIndex] - percentage);
        anchors.forEach((anchor, i) => {
            if (Math.abs(anchor - percentage) < minDiff) {
                minDiff = Math.abs(anchor - percentage);
                nearestIndex = i;
            }
        });

        return colors[nearestIndex];
    }

    const clamped = Math.max(min, Math.min(max, val));
    let colorBinIndex = 0;

    //find the index of the first threshold that is bigger than the value
    const unexceededThreshold = colorScaleBins?.findIndex((n) => clamped < n);

    //if no bigger value is found, return the color for the highest bin
    if (unexceededThreshold === -1) {
        return colors[colorScaleBins.length - 1];
    }

    //otherwise, pick the bin before the threshold that hasn't been exceeded (unless already at 0)
    if (unexceededThreshold > 0) {
        colorBinIndex = unexceededThreshold - 1;
    }

    return colors[colorBinIndex];
};
